import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { formatNumber } from '@boatnav/core';
import {
  listRegions,
  deleteRegionMeta,
  type RegionMeta,
} from '../../lib/db.js';
import { deleteRegionCache } from './downloadEngine.js';
import { RegionPicker } from './RegionPicker.js';
import { swSupported } from './serviceWorker.js';
import { MAX_REGIONS } from './constants.js';
import { loadDepthEdition } from '../route/depthMetaStore.js';
import { formatBytes, shouldNudgeInstall } from '../../lib/platform.js';
import { AccountSection } from '../account/AccountSection.js';
import { PrefsSection } from './PrefsSection.js';
import { AboutSection } from './AboutSection.js';
import { StackHeader } from '../../lib/StackHeader.js';
import { msg } from '../../lib/messages.js';
import './settings.css';

interface StorageInfo {
  usage: number;
  quota: number;
}

export function SettingsScreen(): JSX.Element {
  const [regions, setRegions] = useState<RegionMeta[]>([]);
  const [storage, setStorage] = useState<StorageInfo | null>(null);
  const [picking, setPicking] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [depthEdition, setDepthEdition] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setRegions(await listRegions());
    if (navigator.storage?.estimate) {
      const est = await navigator.storage.estimate();
      setStorage({ usage: est.usage ?? 0, quota: est.quota ?? 0 });
    }
    setDepthEdition(await loadDepthEdition());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onDownloadClick = (): void => {
    // Nudge iOS-in-tab users to install before their first region (spec 04).
    if (regions.length === 0 && shouldNudgeInstall()) {
      setShowInstall(true);
      return;
    }
    setPicking(true);
  };

  const onDelete = async (r: RegionMeta): Promise<void> => {
    await deleteRegionCache(r.id);
    await deleteRegionMeta(r.id);
    await refresh();
  };

  const usedPct = storage && storage.quota > 0 ? Math.round((storage.usage / storage.quota) * 100) : 0;

  return (
    <div className="settings">
      <div className="screen-inner">
        <StackHeader title={msg.settings.title} />

        {!swSupported && <div className="settings__banner">{msg.settings.onlineOnly}</div>}

        <AccountSection />

        <PrefsSection />

        <section className="settings__section">
          <h2 className="settings__section-title">{msg.settings.boatSection}</h2>
          <Link to="/baat" className="settings__navlink">
            <span>{msg.settings.boatLink}</span>
            <ChevronRight size={20} aria-hidden="true" />
          </Link>
        </section>

        <section className="settings__section">
        <h2 className="settings__section-title">{msg.settings.offlineSection}</h2>

        {storage && (
          <div className="settings__storage">
            <div className="settings__storage-row">
              <span>{msg.settings.storageUsed}</span>
              <span className="numeral">
                {formatBytes(storage.usage)} {msg.settings.of} {formatBytes(storage.quota)}
              </span>
            </div>
            <div className="settings__bar">
              <div className="settings__bar-fill" style={{ width: `${usedPct}%` }} />
            </div>
          </div>
        )}

        {regions.length === 0 ? (
          <p className="settings__empty">{msg.settings.noRegions}</p>
        ) : (
          <ul className="settings__regions">
            {regions.map((r) => {
              const staleDepth =
                depthEdition != null && r.depthEdition != null && r.depthEdition !== depthEdition;
              return (
                <li key={r.id} className="region-item">
                  <div className="region-item__body">
                    <div className="region-item__name">
                      {r.name}
                      {!r.complete && <span className="region-item__flag">{msg.settings.incomplete}</span>}
                    </div>
                    <div className="region-item__meta numeral">
                      {formatNumber(r.tileCount, 0)} {msg.settings.tiles} · {formatBytes(r.bytes)}
                    </div>
                    {staleDepth && <p className="region-item__warn">{msg.settings.staleDepth}</p>}
                  </div>
                  <button type="button" className="region-item__delete" onClick={() => void onDelete(r)}>
                    {msg.settings.delete}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {swSupported && (
          <button
            type="button"
            className="btn btn--accent settings__download"
            disabled={regions.length >= MAX_REGIONS}
            onClick={onDownloadClick}
          >
            {msg.settings.downloadRegion}
          </button>
        )}
      </section>

      {picking && (
        <RegionPicker
          onClose={() => {
            setPicking(false);
            void refresh();
          }}
        />
      )}

      <AboutSection />

      {showInstall && (
        <div className="sheet-backdrop" onClick={() => setShowInstall(false)}>
          <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__handle" />
            <h2 className="sheet__title">{msg.install.title}</h2>
            <p className="sheet__body">{msg.install.body}</p>
            <div className="sheet__actions">
              <button
                type="button"
                className="btn btn--accent"
                onClick={() => {
                  setShowInstall(false);
                  setPicking(true);
                }}
              >
                {msg.install.ok}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
